import { isDemoDeployment } from '@/utils/deployment'
import { apiCall, ownerApiCall } from './axiosInstance'
import { actionTypesApi as demoActionTypesApi } from './demo/actionTypesApi'

export type ActionType = {
	type: string
	icon?: string | null
	payload_type: string
	json_schema?: object | null
	json_ui_schema?: object | null
	timeout_seconds?: number | null
	timeout_trigger?: 'queued' | 'in_progress'
	retry_on_timeout_count?: number
	active?: boolean
}

export type PayloadType = 'none' | 'json' | 'file' | 'config'

/**
 * POST /v3/action-types/{name} — CreateActionTypeSchema.
 * `timeout_seconds` takes a plain integer: null is a 400, omitting it defaults to 86400.
 * No `timeout_trigger` here — create always stores `in_progress`.
 */
export type CreateActionTypeRequest = {
	timeout_seconds?: number
	retry_on_timeout_count?: number
	json_schema?: object | null
	json_ui_schema?: object | null
	payload_type?: PayloadType
	icon?: string | null
}

/**
 * PATCH /v3/action-types/{name} — UpdateActionTypeSchema, which is `deny_unknown_fields`:
 * an extra key (`type`, `active`, …) fails the whole request with a 400.
 */
export type UpdateActionTypeRequest = {
	timeout_seconds?: number | null
	timeout_trigger?: 'queued' | 'in_progress'
	retry_on_timeout_count?: number
	json_schema?: object | null
	json_ui_schema?: object | null
	payload_type?: PayloadType
	icon?: string | null
}

export type ActionDropdownType = {
	text: string
	value: string
	icon: string
	payload_type: string
	json_schema?: string
	json_ui_schema?: string
	timeout_seconds?: number | null
}

// `renew_cert` is an internal reprovision type — project UI must never list it as triggerable.
const INTERNAL_ACTION_TYPES = new Set(['renew_cert'])

export const stripInternalActionTypes = (list: ActionType[]): ActionType[] =>
	(Array.isArray(list) ? list : []).filter(actionType => !INTERNAL_ACTION_TYPES.has(actionType.type))

const v3ActionTypesApi = {
	getActionTypes: async (): Promise<ActionType[]> => stripInternalActionTypes(await apiCall('/v3/action-types')),

	getOrgActionTypes: async (organizationId: string): Promise<{ result: string[] }> =>
		ownerApiCall(`/v3/organizations/${encodeURIComponent(organizationId)}/action-types`, { method: 'GET' }),

	/**
	 * POST /v3/action-types/{name}
	 * @param actionType - Action type data with type as the name
	 */
	createActionType: async (actionType: ActionType): Promise<void> => {
		const { type, ...body } = actionType
		return apiCall(`/v3/action-types/${encodeURIComponent(type)}`, {
			method: 'POST',
			data: body as CreateActionTypeRequest,
		})
	},

	/**
	 * Update an existing action type
	 * PATCH /v3/action-types/{name}
	 * @param name - Action type name
	 * @param actionType - Fields to update; the endpoint rejects any key outside this set.
	 */
	updateActionType: async (name: string, actionType: UpdateActionTypeRequest): Promise<void> =>
		apiCall(`/v3/action-types/${encodeURIComponent(name)}`, {
			method: 'PATCH',
			data: actionType,
		}),

	/**
	 * Deactivate an action type
	 * POST /v3/action-types/{name}/deactivate
	 * @param name - Action type name
	 */
	deactivateActionType: async (name: string): Promise<void> =>
		apiCall(`/v3/action-types/${encodeURIComponent(name)}/deactivate`, {
			method: 'POST',
		}),

	/**
	 * Reactivate an action type
	 * POST /v3/action-types/{name}/reactivate
	 * @param name - Action type name
	 */
	reactivateActionType: async (name: string): Promise<void> =>
		apiCall(`/v3/action-types/${encodeURIComponent(name)}/reactivate`, {
			method: 'POST',
		}),

	/**
	 * Delete action type - V3 doesn't have delete, uses deactivate instead
	 * @deprecated Use deactivateActionType instead
	 */
	deleteActionType: async (name: string): Promise<void> => {
		return v3ActionTypesApi.deactivateActionType(name)
	},
}

export const actionTypesApi = isDemoDeployment() ? demoActionTypesApi : v3ActionTypesApi
